import { getConfig } from '@/configs/config-accessor';
import { REQUEST_HEADER_AUTH_KEY, TOKEN_TYPE } from '@/constants/api.constant';
import { Pages } from '@/constants/page.constant';
import store from '@/store';
import { setToken } from '@/store/auth/sessionSlice';
import axios from 'axios';
import isURL from 'validator/lib/isURL';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

const BaseService = axios.create({
  timeout: 60000,
});

export const handleSetApiAuthorizationHeader = (token: string) => `${TOKEN_TYPE}${token}`;

BaseService.interceptors.request.use(
  (config) => {
    const accessToken = store.getState().auth.session.token;

    if (config.headers) {
      if (accessToken) config.headers[REQUEST_HEADER_AUTH_KEY] = handleSetApiAuthorizationHeader(accessToken);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

BaseService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, request } = error;
    if (!!response === false) return Promise.reject(error);

    const statusCode = parseInt(response?.data?.status || '500');
    const requestPathname = isURL(request.responseURL, { require_tld: false }) ? new URL(request.responseURL).pathname : null;

    if (statusCode === 401 && requestPathname !== null && requestPathname !== `/${process.env.REACT_APP_ENV}/auth/refresh`) {
      try {
        const accessToken = store.getState().auth.session.token;
        const newApiPrefix = getApiPrefix();

        const refreshResponse = await BaseService.post(`${newApiPrefix}/auth/refresh`, null, { headers: { [REQUEST_HEADER_AUTH_KEY]: handleSetApiAuthorizationHeader(accessToken) } });
        const refreshResponseData: IResponse<{ token: string }> = refreshResponse.data;
        const newAccessToken = refreshResponseData.data.token;

        store.dispatch(setToken(newAccessToken));

        // Retry the original request with the new token
        error.config.headers[REQUEST_HEADER_AUTH_KEY] = handleSetApiAuthorizationHeader(newAccessToken);
        return BaseService.request(error.config);
      } catch (error) {
        if (window.location.pathname === Pages.ACCESS_DENIED) return (window.location.href = `${Pages.ACCESS_DENIED}?status=${statusCode}`);
        else return Promise.reject(error);
      }
    } else if (statusCode === 401 || statusCode === 403) {
      if (window.location.pathname === Pages.ACCESS_DENIED) return (window.location.href = `${Pages.ACCESS_DENIED}?status=${response.status}`);
      else if (window.location.pathname === Pages.SIGN_IN) return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default BaseService;
